import React, { useEffect, useRef } from 'react';

import { Box, Icon, VStack } from '@rbilabs/universal-components';
import { randomUUID as uuidv4 } from 'expo-crypto';
import { useRouter } from 'expo-router';
import { IPaymentMethod } from 'features/payment/types';
import { mapPaymentMethodToLoyaltyPaymentMethods } from 'features/payment/utils';
import { View } from 'react-native';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants, ActionLink } from 'components/action-button';
import { useCart } from 'hooks/use-cart';
import { isHydrationOrDehydrationInProgress } from 'hooks/use-cart-hydration.ts';
import { CartItem } from 'pages/cart/cart-item';
import { MenuAcceleratorPromotionBanner } from 'pages/menu/components/menu-accelerator-badge/promotion-banner';
import { useCartContext } from 'state/cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getServerOrderSubtotalBeforeOfferDiscounts } from 'state/loyalty/hooks/utils/helpers';
import { useRemoveExpiredOfferFromCart } from 'state/loyalty/offers/hooks/use-remove-expired-offer-from-cart';
import { useServiceModeContext } from 'state/service-mode';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { routes } from 'utils/routing';

import Totals from '../../../experiments/checkout-v2/control/pages/cart/your-cart/totals';
import { useLoyaltyCartEntriesEvaluation } from '../hooks/use-loyalty-cart-entries-evaluation';
import { Upsell } from '../upsell';

import DonationContainer from './donation-container';
import FoodwareContainer from './foodware-container';
import PriceMismatchInternalOnly from './price-mismatch-internal-only';
import { ActionButtonsContainer, ActionLinkContainer } from './styled';
import YourCartHeading from './your-cart-heading';

interface IYourCartProps {
  checkoutPaymentMethod?: IPaymentMethod | null;
  readOnly?: boolean;
  showAddItemsBtn?: boolean;
  showAddExtrasBtn?: boolean;
  showCartPromoCodes?: boolean;
}

const YourCart: React.FC<React.PropsWithChildren<IYourCartProps>> = ({
  checkoutPaymentMethod,
  readOnly = false,
  showAddItemsBtn = true,
  showAddExtrasBtn = true,
}) => {
  const { cartEntries, calculateCartTotalWithDiscount, addItemToCart } = useCart();
  const isDonationsEnabled = useFlag(LaunchDarklyFlag.ENABLE_FOUNDATION_DONATIONS);
  const router = useRouter();
  const { serviceMode } = useServiceModeContext();

  const {
    pricedAndAvailable = true,
    priceOrderSuccess,
    priceMutationError,
    serverOrder,
    shouldSkipPriceOrder,
    a11yMessage,
    announceA11yCartMessage,
    isOrderPriceLoading,
  } = useCartContext();

  const { loyaltyUser } = useLoyaltyUser();
  const evaluateLoyaltyCartEntries = useLoyaltyCartEntriesEvaluation();
  const showActionButtons = showAddItemsBtn || showAddExtrasBtn;
  const enableAddExtras = useFlag(LaunchDarklyFlag.ENABLE_ADD_EXTRAS);
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const dispatch = useAppDispatch();

  const appliedRewardsCount = useRef(0);
  const cartEntriesCount = useRef(0);

  const { isCartTotalNegative = false } = calculateCartTotalWithDiscount
    ? calculateCartTotalWithDiscount()
    : {};

  const positiveCartTotal = isCartTotalNegative
    ? 0
    : getServerOrderSubtotalBeforeOfferDiscounts(serverOrder);

  useEffect(() => {
    // Resetting offer feedback on mount to avoid carrying over feedback from offer list
    dispatch(actions.loyalty.resetOfferFeedbackMap());
  }, [dispatch]);

  useEffect(() => {
    /**
     * If an unauthenticated user adds items to their cart and then login,
     * they'll likely be part of the Cart v2 experiment.
     * In this case, we'll need to adjust cart entries with quantities greater than 1 to fit the new Cart v2 flow.
     */

    const cartEntriesToBeExpanded = cartEntries.filter(item => item.quantity && item.quantity > 1);

    if (cartEntriesToBeExpanded.length) {
      const expandedCartEntries: ILegacyCartEntry[] = [];

      cartEntriesToBeExpanded.forEach(item => {
        const expandedCartEntry = [...Array(item.quantity)].map(() => {
          const newId = uuidv4();
          return {
            ...item,
            quantity: 1,
            cartId: newId,
            lineId: newId,
          };
        });
        expandedCartEntries.push(...expandedCartEntry);
      });

      expandedCartEntries.forEach(item => {
        dispatch(actions.ordering.addCartEntry(item));
      });

      // Remove the original expanded cart entries to improve the order of the cart items.
      dispatch(
        actions.ordering.removeCartEntries(
          new Set(cartEntriesToBeExpanded.map(item => item.cartId))
        )
      );
    }
  }, [addItemToCart, cartEntries, dispatch]);

  const { indexedMenu } = useUnifiedMenuContext();
  const isUnifiedMenuLoaded = indexedMenu.isLoaded();

  // TODO: Replace with useEffectEvent when we upgrade to React 19.2
  // https://react.dev/reference/react/useEffectEvent
  const runIncentivesEvaluationCb = () => {
    if (loyaltyUser?.id) {
      const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);

      evaluateLoyaltyCartEntries(loyaltyUser?.id, cartEntries, positiveCartTotal, paymentMethod);
    }
  };
  const runIncentivesEvaluation = useRef(runIncentivesEvaluationCb);
  runIncentivesEvaluation.current = runIncentivesEvaluationCb;

  useEffect(() => {
    if (isUnifiedMenuLoaded && priceOrderSuccess && !priceMutationError) {
      runIncentivesEvaluation.current();
    }
  }, [isUnifiedMenuLoaded, priceMutationError, priceOrderSuccess]);

  useEffect(() => {
    if (pricedAndAvailable) {
      const validCartEntriesCount =
        // Donation and Extras entries should not be taken into account for querying offers
        cartEntries?.filter(({ isExtra, isDonation }: ILegacyCartEntry) => !isDonation && !isExtra)
          .length ?? 0;
      const appliedRewardsLength = Object.keys(appliedLoyaltyRewards).length;

      appliedRewardsCount.current = appliedRewardsLength;
      cartEntriesCount.current = validCartEntriesCount;
    }
  }, [appliedLoyaltyRewards, cartEntries, pricedAndAvailable]);

  // TODO: https://rbictg.atlassian.net/browse/BKPE-25114
  useRemoveExpiredOfferFromCart();

  return (
    <Box>
      <YourCartHeading />

      <MenuAcceleratorPromotionBanner entries={cartEntries.map(entry => entry._id)} isFromCart />

      <Box accessibilityLabel="cart-section-heading-label">
        <View
          accessible={false}
          accessibilityLabel={a11yMessage}
          accessibilityLiveRegion="polite"
          style={{
            width: 0,
            height: 0,
            opacity: 0,
          }}
          testID="cart-accessibility-message-announcer"
        />

        <VStack testID="cart-items">
          {cartEntries.map((item: ILegacyCartEntry) => (
            <CartItem
              item={item}
              key={item.cartId || item._id}
              readOnly={readOnly}
              rewardApplied={appliedLoyaltyRewards[item.cartId]?.timesApplied > 0}
              showReadOnlyQuantity
              onAnnounceA11yMessage={announceA11yCartMessage}
            />
          ))}
        </VStack>

        {showActionButtons && (
          <ActionButtonsContainer>
            {showAddExtrasBtn && enableAddExtras && (
              <ActionLinkContainer>
                <ActionLink
                  to={routes.cartAddExtras}
                  startIcon={<Icon variant="add" {...hiddenAccessibilityPlatformProps} />}
                  variant={ActionButtonVariants.OUTLINE}
                  disabled={isOrderPriceLoading}
                >
                  {t('addCondiments')}
                </ActionLink>
              </ActionLinkContainer>
            )}

            {showAddItemsBtn && (
              <ActionLinkContainer>
                <ActionButton
                  onPress={() => router.navigate(routes.menu)}
                  startIcon={<Icon variant="add" {...hiddenAccessibilityPlatformProps} />}
                  variant={ActionButtonVariants.OUTLINE}
                  eventAttributes={{
                    Name: t('addItemsLowerCase'),
                  }}
                  disabled={isOrderPriceLoading}
                >
                  {t('addItems')}
                </ActionButton>
              </ActionLinkContainer>
            )}
          </ActionButtonsContainer>
        )}

        <Box marginTop={{ base: '$2', md: '$4' }}>
          <Upsell isInCart />
          <FoodwareContainer />
          {isDonationsEnabled && <DonationContainer isLoading={isOrderPriceLoading} />}
        </Box>

        <PriceMismatchInternalOnly />

        {pricedAndAvailable && (
          <Totals
            isLoading={isOrderPriceLoading || isHydrationOrDehydrationInProgress}
            serverOrder={serverOrder}
            serviceMode={serviceMode}
            shouldSkipPriceOrder={shouldSkipPriceOrder}
          />
        )}
      </Box>
    </Box>
  );
};

export default React.memo(YourCart);
